import { useState } from 'react'
import {
  HeaderChildrenContainer,
  IconContainer,
  LogTitle,
  TitleContainer,
  Wrapper,
} from './components/display/commonStyledComponents'
import {
  Button,
  Check,
  EmptyState,
  Form,
  FormCard,
  NoDataIcon,
  PageHeader,
  Tag,
  Trash,
} from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import { generalConstants } from 'locale/general'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import IncidentLogFormFields from './components/forms/IncidentLogFormFields'
import { useNavigate } from 'react-router-dom'
import { incidentReportsConstants } from 'locale/incidentReports'
import RemoveIncidentModal from './components/modals/RemoveIncidentModal'
import { useParams } from 'react-router-dom'
import { Spinner } from 'components/generic'
import {
  IncidentReportUpdateBody,
  Home,
  LogUpdateBody,
  UserRole,
} from '@lmc/ts-common'
import dayjs from 'dayjs'
import useLog from 'hooks/useLog/useLog'
import useGoals from 'screens/Residents/screens/ResidentsGoalsScreen/components/useGoals/useGoals'
import { useSelector } from 'react-redux'
import useIncidentReportByLog from 'hooks/useIncidentReport/useIncidentReportByLog'
import { tagColorMap } from './utils'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import useIncidentTypeOptions from 'hooks/useIncidentTypeOptions'
import IncidentTypeChangeModal from './components/modals/IncidentTypeChangeModal'
import { useRoleAccess } from 'components/AccessControl'

const INCIDENT_CATEGORY_ITEM = '5e867702b349f776c8d48c34'

const IncidentLogEditScreen = () => {
  const [form] = AntdForm.useForm()
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const navigate = useNavigate()
  const { id: incidentLogId } = useParams()
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home,
  )

  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin],
    [
      {
        i: 'delete',
        this: 'Incident',
      },
    ],
  )

  const handleOpen = () => {
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const {
    log: incidentLog,
    isLogLoading,
    updateLog,
    removeLog,
    isRemoveLoading,
  } = useLog({
    logId: incidentLogId,
    onUpdateSuccess: () => {
      showToast({
        title: t(incidentReportsConstants.LOG_UPDATED),
      })
      navigate(`../`, { relative: 'path' })
    },
    onRemoveSuccess: () => {
      showToast({
        title: t(incidentReportsConstants.LOG_REMOVED),
      })
      navigate(`../`, { relative: 'path' })
    },
  })

  const { incidentReport, isIncidentReportLoading } = useIncidentReportByLog({
    incidentLogId,
  })

  const { goals } = useGoals(incidentLog?.resident as unknown as string)

  const incidentTypes = useIncidentTypeOptions(INCIDENT_CATEGORY_ITEM)

  if (isLogLoading || isIncidentReportLoading) return <Spinner />
  if (!incidentLog || !incidentReport)
    return (
      <EmptyState
        icon={<NoDataIcon />}
        title={t(incidentReportsConstants.LOG_NOT_FOUND)}
      />
    )

  const initialValues: Partial<IncidentReportUpdateBody> = {
    ...incidentLog,
    dateLogged: dayjs(incidentLog.timeLogged),
    timeLogged: dayjs(incidentLog.timeLogged),
    item: incidentReport.log.item?.id,
  }

  const onSave = async ({ item, ...values }: LogUpdateBodyExtended) => {
    try {
      const datePart = dayjs(values.dateLogged)
      const timePart = dayjs(values.timeLogged)
      const formattedDate = datePart
        .hour(timePart.hour())
        .minute(timePart.minute())
        .second(timePart.second())
        .millisecond(timePart.millisecond())

      setIsUpdateLoading(true)
      setIsDirty(false)
      const hasLogItemProperty = !!item && Object.keys(item).length > 0

      await updateLog({
        ...values,
        ...(hasLogItemProperty && { item }),
        timeLogged: formattedDate,
      })
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error',
      })
    }
    setIsUpdateLoading(false)
  }

  const handleOnUpdateFinish = (values: LogUpdateBodyExtended) => {
    if (values.item !== incidentLog.item) {
      handleOpen()
    } else {
      onSave(values)
    }
  }

  const handleRemoveLog = async () => {
    try {
      await removeLog()
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error',
      })
    }
  }

  const handleFormValuesChange = (
    values: Partial<IncidentReportUpdateBody>,
  ) => {
    if (values) {
      setIsDirty(true)
    }
  }

  return (
    <>
      <PageHeader
        sticky
        onBack={() => navigate('../', { relative: 'path' })}
        title={
          <TitleContainer>
            <IconContainer
              src={
                (incidentReport.log.item?.icon ||
                  incidentReport.log.category?.icon) as unknown as string
              }
              $color={incidentReport.log.category.color}
              alt="icon"
            />
            {incidentLog.title}
            <Tag color={tagColorMap[incidentReport.status]}>
              {t(incidentReportsConstants[incidentReport.status])}
            </Tag>
          </TitleContainer>
        }
      >
        <HeaderChildrenContainer>
          <Button
            variant="danger"
            onClick={() => setIsRemoveModalOpen(true)}
            disabledByPermission={!hasRolePermission}
          >
            <Trash />
            {t(incidentReportsConstants.REMOVE)}
          </Button>
          <Button onClick={() => navigate('../', { relative: 'path' })}>
            {t(incidentReportsConstants.CANCEL)}
          </Button>
          <Button
            variant="primary"
            type="submit"
            onClick={form.submit}
            isLoading={isUpdateLoading}
          >
            {t(incidentReportsConstants.SAVE)} <Check color="white" />
          </Button>
        </HeaderChildrenContainer>
      </PageHeader>
      <Wrapper>
        <Form
          form={form}
          onFinish={handleOnUpdateFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
          onFinishFailed={({ errorFields }) =>
            errorFields.map((errorField) =>
              errorField.errors.map((err) =>
                showToast({ title: err, type: 'error' }),
              ),
            )
          }
        >
          <FormCard
            title={
              <LogTitle level={3}>{t(incidentReportsConstants.LOG)}</LogTitle>
            }
            subtitle={`${t(incidentReportsConstants.LOGGED_BY)} ${
              incidentReport.log.loggedBy?.name || ''
            }`}
          >
            <IncidentLogFormFields
              goals={goals}
              locations={home.locations}
              incidentTypes={incidentTypes}
              // @ts-ignore
              hasLocationsEnabled={home?.features?.hasHomeLocations?.enabled}
            />
          </FormCard>
        </Form>
        <NavigationModal canNavigate={!isDirty} />
        <RemoveIncidentModal
          open={isRemoveModalOpen}
          onConfirm={handleRemoveLog}
          onCancel={() => setIsRemoveModalOpen(false)}
          isRemoveLoading={isRemoveLoading}
        />
        <IncidentTypeChangeModal
          open={isModalVisible}
          handleCancel={handleCancel}
          onConfirm={() => onSave(form.getFieldsValue())}
        />
      </Wrapper>
    </>
  )
}

interface LogUpdateBodyExtended extends Partial<LogUpdateBody> {
  dateLogged: string
}

export default IncidentLogEditScreen
